import { MiniCompany, MemberCompany } from "./types/company";
import { User, Agent } from "./types/user";
import { ShopifyAccount } from "./types/shopify";
import { Brand, PPAccount } from "./types/paymentProcessor";

// 1️⃣ Define your state type
export interface State {
  user: User | null;
  miniCompanies: MiniCompany[];
  memberCompanies: MemberCompany[];
  brands: Brand[];
  brandsCompanyId: number | null;
  shopifyAccounts: ShopifyAccount[];
  ppAccounts: PPAccount[];
  agents: Agent[];
  // Client-admin billing health: true when company is enrolled but has no default
  // payment method in Stripe. null = not yet loaded / not applicable.
  billingNeedsPaymentMethod: boolean | null;
  // Client dispute-management flag. null = not loaded / not applicable (non-client).
  // false hides dispute metrics and the disputes page from client users.
  disputesEnabled: boolean | null;
  // Client order-insights flag. null = not loaded / not applicable (non-client).
  // false hides Order Insights from the Deflections nav for client users.
  orderInsightsEnabled: boolean | null;
  // Client VAMP-rates flag. null = not loaded / not applicable (non-client).
  // false hides the VAMP rates dashboard tab from client users.
  vampRatesEnabled: boolean | null;
  // The three flags above stay null when /api/client-settings fails, so gated
  // surfaces must read this to tell "still loading" from "we never found out".
  companySettingsFailed: boolean;
}

// The client entitlement flags, as one unit: they are always fetched and applied together.
export interface CompanySettingsFlags {
  disputesEnabled: boolean;
  orderInsightsEnabled: boolean;
  vampRatesEnabled: boolean;
}

// 2️⃣ Define action types
export type Action =
  | { type: "SET_USER"; user: any }
  | { type: "SET_MINI_COMPANIES"; miniCompanies: MiniCompany[] }
  | { type: "SET_MEMBER_COMPANIES"; memberCompanies: MemberCompany[] }
  | { type: "SET_BRANDS"; brands: Brand[]; brandsCompanyId: number | null }
  | { type: "SET_SHOPIFY_ACCOUNTS"; shopifyAccounts: ShopifyAccount[] }
  | { type: "SET_PP_ACCOUNTS"; ppAccounts: PPAccount[] }
  | { type: "SET_AGENTS"; agents: Agent[] }
  | { type: "SET_BILLING_NEEDS_PAYMENT_METHOD"; billingNeedsPaymentMethod: boolean | null }
  // null clears the flags back to "not yet known" (company switch).
  | { type: "SET_COMPANY_SETTINGS"; settings: CompanySettingsFlags | null }
  | { type: "SET_COMPANY_SETTINGS_FAILED" }
  | { type: "RESET_USER" };

// 3️⃣ Initial state
export const initialState: State = {
  user: null,
  miniCompanies: [],
  memberCompanies: [],
  brands: [],
  brandsCompanyId: null,
  shopifyAccounts: [],
  ppAccounts: [],
  agents: [],
  billingNeedsPaymentMethod: null,
  disputesEnabled: null,
  orderInsightsEnabled: null,
  vampRatesEnabled: null,
  companySettingsFailed: false,
};

// 4️⃣ Reducer function
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_USER":
      return {
        ...state,
        user: action.user,
      };
    case "RESET_USER":
      return initialState;
    case "SET_MINI_COMPANIES":
      return {
        ...state,
        miniCompanies: action.miniCompanies,
      };
    case "SET_MEMBER_COMPANIES":
      return {
        ...state,
        memberCompanies: action.memberCompanies,
      };
    case "SET_BRANDS":
      return {
        ...state,
        brands: action.brands,
        brandsCompanyId: action.brandsCompanyId,
      };
    case "SET_SHOPIFY_ACCOUNTS":
      return {
        ...state,
        shopifyAccounts: action.shopifyAccounts,
      };
    case "SET_PP_ACCOUNTS":
      return {
        ...state,
        ppAccounts: action.ppAccounts,
      };
    case "SET_AGENTS":
      return {
        ...state,
        agents: action.agents,
      };
    case "SET_BILLING_NEEDS_PAYMENT_METHOD":
      return {
        ...state,
        billingNeedsPaymentMethod: action.billingNeedsPaymentMethod,
      };
    case "SET_COMPANY_SETTINGS":
      return {
        ...state,
        disputesEnabled: action.settings?.disputesEnabled ?? null,
        orderInsightsEnabled: action.settings?.orderInsightsEnabled ?? null,
        vampRatesEnabled: action.settings?.vampRatesEnabled ?? null,
        companySettingsFailed: false,
      };
    case "SET_COMPANY_SETTINGS_FAILED":
      return {
        ...state,
        companySettingsFailed: true,
      };
    default:
      return state;
  }
};

export default reducer;
